import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Story } from '../../schemas/story.js';

import { locatorOf, toPointer } from '../../core/issues.js';
import { isScoreRoute } from '../shared/_helpers/exitRoutes.js';
import { findSimilarString } from './_helpers/findSimilarString.js';

export const exitRouteTargets: Rule<Story> = {
  code: 'story.exitRoutes.unknownTarget',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];

    const nodeIds = input.nodes.map(n => n.id);
    const nodeIdSet = new Set(nodeIds);

    input.nodes.forEach((node, nodeIdx) => {
      const routes = node.exitRoutes;
      if (!routes) {
        return;
      }

      for (const [outcomeId, targetNodeId] of Object.entries(routes)) {
        if (isScoreRoute(targetNodeId)) {
          continue;
        }
        if (nodeIdSet.has(targetNodeId)) {
          continue;
        }

        const path: (string | number)[] = ['nodes', nodeIdx, 'exitRoutes', outcomeId];
        const similar = typeof targetNodeId === 'string' ? findSimilarString(targetNodeId, nodeIds) : null;

        const fix = similar
          ? {
              kind: 'replace' as const,
              value: similar,
              rationale: `Fixed exit route in ${node.id}: ${outcomeId} -> ${similar}`,
            }
          : {
              kind: 'remove' as const,
              rationale: `Removed broken exit route in ${node.id}: ${outcomeId} -> ${targetNodeId}`,
            };

        issues.push({
          code: 'story.exitRoutes.unknownTarget',
          severity: 'error',
          message: `Node ${node.id}: exitRoute '${outcomeId}' -> '${targetNodeId}' points to non-existent node`,
          path,
          pointer: toPointer(path),
          locator: locatorOf({ nodeId: node.id, branchId: node.branchId }),
          currentValue: targetNodeId,
          constraint: { kind: 'reference', detail: 'exitRoute target must be an existing node id' },
          fix,
        });
      }
    });

    return issues;
  },
};
